<template>
  <div class="stall-after-sales">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="back-btn" @click="goBack">← {{ $t('common.back') }} / Back</div>
      <h1>{{ $t('stall.myAfterSales') }} / After Sales</h1>
      <div class="sub">{{ $t('afterSales.totalRecords') }} {{ total }} {{ $t('afterSales.totalRecordsSuffix') }}</div>
    </div>

    <div v-loading="loading" class="content">
      <!-- 统计卡片 -->
      <div class="stats-row">
        <div class="stat-card warn">
          <div class="stat-num">{{ stats.pending }}</div>
          <div class="stat-label">{{ $t('afterSales.pendingReview') }}</div>
        </div>
        <div class="stat-card info">
          <div class="stat-num">{{ stats.reviewed }}</div>
          <div class="stat-label">{{ $t('afterSales.processing') }}</div>
        </div>
        <div class="stat-card success">
          <div class="stat-num">S$ {{ totalRefund }}</div>
          <div class="stat-label">{{ $t('afterSales.totalRefund') }}</div>
        </div>
      </div>

      <!-- 筛选标签 -->
      <div class="filter-tabs">
        <span
          v-for="tab in filterTabs"
          :key="tab.value"
          class="filter-tab"
          :class="{ active: filterStatus === tab.value }"
          @click="filterStatus = tab.value; page = 0; loadList()"
        >
          {{ tab.label }}
        </span>
      </div>

      <!-- 售后卡片列表 -->
      <div v-if="list.length > 0">
        <div
          v-for="item in list"
          :key="item.id"
          class="as-card"
          @click="goDetail(item.id)"
        >
          <div class="as-card-header">
            <span class="as-no">{{ item.afterSalesNo }}</span>
            <span class="as-status" :class="statusClass(item.status)">{{ statusText(item.status) }}</span>
          </div>
          <div class="as-card-body">
            <div class="as-left">
              <div class="as-reason">{{ reasonIcon(item.reasonCode) }} {{ reasonText(item.reasonCode) }}</div>
              <div class="as-order">{{ $t('afterSales.relatedOrder') }}: {{ item.orderNo }}</div>
              <div class="as-time">{{ item.requestedAt }} · {{ schemeText(item.resolutionType) || $t('afterSales.waitingReview') }}</div>
            </div>
            <div class="as-right">
              <div class="as-amount">S$ {{ centsToAmount(item.refundAmount) }}</div>
              <div class="as-sub">{{ resolutionStatusText(item.resolutionStatus) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <div class="empty-icon">📋</div>
        <div class="empty-text">{{ $t('afterSales.noRecords') }}</div>
        <div class="empty-sub">{{ $t('afterSales.noRecordsHint') }}</div>
      </div>

      <!-- 分页 -->
      <div v-if="totalPages > 1" class="pagination">
        <el-button size="mini" :disabled="page === 0" @click="page--; loadList()">{{ $t('afterSales.prevPage') }}</el-button>
        <span>{{ page + 1 }} / {{ totalPages }}</span>
        <el-button size="mini" :disabled="page >= totalPages - 1" @click="page++; loadList()">{{ $t('afterSales.nextPage') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { getAfterSalesList } from '@/services/afterSales';

export default {
  name: 'StallAfterSales',
  data() {
    return {
      loading: false,
      list: [],
      page: 0,
      pageSize: 20,
      totalPages: 1,
      total: 0,
      filterStatus: '',
      stats: { pending: 0, reviewed: 0 }
    };
  },
  computed: {
    filterTabs() {
      return [
        { label: this.$t('common.all'), value: '' },
        { label: this.$t('afterSales.pendingReview'), value: 'PENDING_REVIEW' },
        { label: this.$t('afterSales.processing'), value: 'REVIEWED' },
        { label: this.$t('afterSales.completed'), value: 'COMPLETED' }
      ];
    },
    totalRefund() {
      return this.list
        .filter(item => item.status === 'COMPLETED')
        .reduce((sum, item) => sum + (item.refundAmount || 0), 0) / 100;
    }
  },
  created() {
    this.loadList();
  },
  methods: {
    async loadList() {
      this.loading = true;
      try {
        const params = {
          status: this.filterStatus || undefined,
          page: this.page,
          size: this.pageSize
        };
        const orderId = this.$route.query.orderId;
        if (orderId) params.orderId = orderId;
        const res = await getAfterSalesList(params);
        this.list = res.content || [];
        this.totalPages = res.totalPages || 1;
        this.total = res.totalElements || 0;

        this.stats.pending = this.list.filter(i => i.status === 'PENDING_REVIEW').length;
        this.stats.reviewed = this.list.filter(i => i.status === 'REVIEWED').length;
      } catch (e) {
        this.$message.error(this.$t('afterSales.loadFailed') + ': ' + (e.message || e));
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push('/stall/orders');
    },
    goDetail(id) {
      this.$router.push(`/stall/after-sales/${id}`);
    },
    statusClass(status) {
      const map = {
        PENDING_REVIEW: 'status-pending',
        REVIEWED: 'status-reviewed',
        COMPLETED: 'status-completed',
        REJECTED: 'status-rejected',
        CANCELLED: 'status-cancelled'
      };
      return map[status] || '';
    },
    statusText(status) {
      const map = {
        PENDING_REVIEW: '⏳ ' + this.$t('afterSales.pendingReview'),
        REVIEWED: '🔄 ' + this.$t('afterSales.processing'),
        COMPLETED: '✅ ' + this.$t('afterSales.completed'),
        REJECTED: '❌ ' + this.$t('afterSales.rejected'),
        CANCELLED: '🚫 ' + this.$t('afterSales.cancelled')
      };
      return map[status] || status;
    },
    reasonIcon(code) {
      const map = {
        DAMAGED: '📦', WRONG_ITEM: '❌', NOT_RECEIVED: '❓',
        WRONG_SPEC: '📏', SHORT_DELIVERY: '📉', DONT_WANT: '🙅', OTHER: '📝'
      };
      return map[code] || '📋';
    },
    reasonText(code) {
      const map = {
        DAMAGED: this.$t('afterSales.reasonDamaged'), WRONG_ITEM: this.$t('afterSales.reasonWrongItem'), NOT_RECEIVED: this.$t('afterSales.reasonNotReceived'),
        WRONG_SPEC: this.$t('afterSales.reasonWrongSpec'), SHORT_DELIVERY: this.$t('afterSales.reasonShortDelivery'), DONT_WANT: this.$t('afterSales.reasonDontWant'), OTHER: this.$t('afterSales.reasonOther')
      };
      return map[code] || code;
    },
    schemeText(type) {
      if (!type) return this.$t('afterSales.waitingReview');
      const map = {
        RETURN_REFUND: this.$t('afterSales.schemeReturnRefund'), EXCHANGE: this.$t('afterSales.schemeExchange'), RESEND: this.$t('afterSales.schemeResend'),
        PARTIAL_REFUND: this.$t('afterSales.schemePartialRefund'), RELIEF: this.$t('afterSales.schemeRelief'), NO_ACTION: this.$t('afterSales.schemeNoAction')
      };
      return this.$t('afterSales.resolutionLabel') + ': ' + (map[type] || type);
    },
    resolutionStatusText(status) {
      if (!status) return '';
      const map = {
        PICKING_UP: this.$t('afterSales.pickingUp'), RETURN_RECEIVED: this.$t('afterSales.returnReceived'), INSPECTING: this.$t('afterSales.inspecting'),
        REFUND_PROCESSING: this.$t('afterSales.refundProcessing'), EXCHANGE_SHIPPING: this.$t('afterSales.exchangeShipping'),
        EXCHANGE_DELIVERED: this.$t('afterSales.exchangeDelivered'), RESEND_SHIPPING: this.$t('afterSales.resendShipping'),
        RESEND_DELIVERED: this.$t('afterSales.resendDelivered')
      };
      return map[status] || status;
    },
    centsToAmount(cents) {
      if (!cents) return '0.00';
      return (cents / 100).toFixed(2);
    }
  }
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #2d6a4f, #52b788);
  color: #fff;
  padding: calc(16px + env(safe-area-inset-top, 0px));
}
.back-btn { font-size: 12px; opacity: 0.8; cursor: pointer; margin-bottom: 4px; }
.back-btn:hover { opacity: 1; }
.page-header h1 { font-size: 17px; }
.page-header .sub { font-size: 12px; opacity: 0.8; }

.content { max-width: 420px; margin: 0 auto; padding-bottom: 20px; background: #f5f5f5; min-height: calc(100vh - 80px); }

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 12px;
}
.stat-card {
  text-align: center;
  padding: 14px 8px;
  background: #fff;
  border-radius: 8px;
}
.stat-num { font-size: 20px; font-weight: 700; color: #2d6a4f; }
.stat-card.warn .stat-num { color: #fa8c16; }
.stat-card.info .stat-num { color: #1890ff; }
.stat-card.success .stat-num { color: #52c41a; }
.stat-label { font-size: 11px; color: #999; margin-top: 4px; }

.filter-tabs {
  display: flex;
  background: #fff;
  padding: 0 12px;
}
.filter-tab {
  padding: 10px 12px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}
.filter-tab.active { color: #2d6a4f; font-weight: 600; border-bottom-color: #2d6a4f; }

.as-card {
  background: #fff;
  margin: 8px;
  border-radius: 8px;
  padding: 14px;
  cursor: pointer;
}
.as-card:active { background: #f0f7f4; }

.as-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.as-no { font-size: 12px; color: #666; font-family: monospace; }
.as-status { font-size: 11px; padding: 2px 8px; border-radius: 10px; }
.status-pending { background: #fff7e6; color: #fa8c16; }
.status-reviewed { background: #e6f7ff; color: #1890ff; }
.status-completed { background: #f6ffed; color: #52c41a; }
.status-rejected { background: #fff2f0; color: #ff4d4f; }
.status-cancelled { background: #fafafa; color: #999; }

.as-card-body { display: flex; justify-content: space-between; }
.as-left { flex: 1; }
.as-reason { font-size: 13px; margin-bottom: 4px; }
.as-order { font-size: 11px; color: #457b9d; margin-top: 2px; }
.as-time { font-size: 11px; color: #bbb; margin-top: 2px; }
.as-right { text-align: right; margin-left: 8px; }
.as-amount { font-size: 15px; color: #ff4d4f; font-weight: 600; }
.as-sub { font-size: 11px; color: #1890ff; margin-top: 2px; }

.empty-state { text-align: center; padding: 60px 20px; color: #999; }
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-text { font-size: 14px; }
.empty-sub { font-size: 12px; margin-top: 4px; }

.pagination { display: flex; justify-content: center; align-items: center; gap: 12px; padding: 16px; }
</style>
